import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { createOffer, updateOffer, getOffer } from '../../services/offers'
import { getUser, logout } from '../../services/helpers'
import { uploadOfferImage, uploadOfferVideo } from '../../services/upload'
import {
  ChartBarIcon,
  CalendarIcon,
  PlusCircleIcon,
  TagIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'

const OfferFormPage = () => {
  const { offerId } = useParams()
  const navigate = useNavigate()
  const isEdit = !!offerId
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center' }
  ]

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    normalPrice: '',
    discountedPrice: '',
    quantity: '',
    validFrom: '',
    validUntil: '',
    terms: '',
    tags: '',
    images: [],
    videos: []
  })

  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(isEdit)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')
  const [uploadingImages, setUploadingImages] = useState(false)
  const [uploadingVideos, setUploadingVideos] = useState(false)

  useEffect(() => {
    if (isEdit) {
      fetchOffer()
    }
  }, [offerId])

  const fetchOffer = async () => {
    setFetchLoading(true)
    const res = await getOffer(offerId)
    if (res.status === 200) {
      const offer = res.data
      setFormData({
        title: offer.title || '',
        description: offer.description || '',
        category: offer.category || '',
        normalPrice: offer.normalPrice || '',
        discountedPrice: offer.discountedPrice || '',
        quantity: offer.quantity || '',
        validFrom: offer.validFrom ? new Date(offer.validFrom).toISOString().slice(0, 16) : '',
        validUntil: offer.validUntil ? new Date(offer.validUntil).toISOString().slice(0, 16) : '',
        terms: offer.terms || '',
        tags: offer.tags ? offer.tags.join(', ') : '',
        images: offer.images || [],
        videos: offer.videos || []
      })
    } else {
      setMessage('Failed to load offer')
      setMessageType('error')
    }
    setFetchLoading(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    // Validate prices
    const normalPrice = parseFloat(formData.normalPrice)
    const discountedPrice = parseFloat(formData.discountedPrice)

    if (discountedPrice >= normalPrice) {
      setMessage('Discounted price must be less than normal price')
      setMessageType('error')
      setLoading(false)
      return
    }

    // Prepare payload
    const payload = {
      title: formData.title,
      description: formData.description,
      category: formData.category,
      normalPrice,
      discountedPrice,
      quantity: parseInt(formData.quantity),
      validFrom: formData.validFrom ? new Date(formData.validFrom).toISOString() : undefined,
      validUntil: formData.validUntil ? new Date(formData.validUntil).toISOString() : undefined,
      terms: formData.terms,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
      images: formData.images || [],
      videos: formData.videos || []
    }

    let res
    if (isEdit) {
      res = await updateOffer(offerId, payload)
    } else {
      res = await createOffer(payload)
    }

    if (res.status === 200 || res.status === 201) {
      setMessage(isEdit ? 'Offer updated successfully' : 'Offer created successfully')
      setMessageType('success')
      setTimeout(() => {
        navigate('/center/offers')
      }, 1500)
    } else {
      setMessage(res.error || 'Failed to save offer')
      setMessageType('error')
    }
    setLoading(false)
  }

  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
          <p className="mt-3 text-sm text-slate-500">Loading offer...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'offers'
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">{isEdit ? 'Edit Offer' : 'Create Offer'}</span>
          <div className="w-6" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900">{isEdit ? 'Edit Offer' : 'Create New Offer'}</h1>
              <p className="text-slate-600 mt-1">Fill in the details below to {isEdit ? 'update' : 'create'} your offer</p>
            </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
            messageType === 'error' 
              ? 'border-red-200 bg-red-50 text-red-800' 
              : messageType === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-emerald-100 bg-emerald-50 text-emerald-800'
          }`}>
            {message}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g., 50% Off All Pizzas"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Describe your offer in detail..."
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Category
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Select category</option>
              <option value="food">Food & Drinks</option>
              <option value="entertainment">Entertainment</option>
              <option value="services">Services</option>
              <option value="products">Products</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Normal Price (₦) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="normalPrice"
                value={formData.normalPrice}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="5000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Discounted Price (₦) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="discountedPrice"
                value={formData.discountedPrice}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="2500"
              />
            </div>
          </div>

          {/* Discount Preview */}
          {formData.normalPrice && formData.discountedPrice && parseFloat(formData.discountedPrice) < parseFloat(formData.normalPrice) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <strong>Discount:</strong> {Math.round(((parseFloat(formData.normalPrice) - parseFloat(formData.discountedPrice)) / parseFloat(formData.normalPrice)) * 100)}% OFF
                <br />
                <strong>Savings:</strong> ₦{(parseFloat(formData.normalPrice) - parseFloat(formData.discountedPrice)).toLocaleString()}
              </p>
            </div>
          )}

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Available Quantity <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              required
              min="1"
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="100"
            />
          </div>

          {/* Validity Period */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Valid From
              </label>
              <input
                type="datetime-local"
                name="validFrom"
                value={formData.validFrom}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Valid Until
              </label>
              <input
                type="datetime-local"
                name="validUntil"
                value={formData.validUntil}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Terms */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Terms & Conditions
            </label>
            <textarea
              name="terms"
              value={formData.terms}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Any special terms or conditions..."
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="pizza, discount, weekend"
            />
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Images
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || [])
                if (files.length === 0) return

                setUploadingImages(true)
                setMessage('')
                const uploadPromises = files.map(file => uploadOfferImage(file))
                
                try {
                  const results = await Promise.all(uploadPromises)
                  const successfulUploads = results
                    .filter(res => res.status === 200 && res.data?.imageUrl)
                    .map(res => res.data.imageUrl)
                  
                  if (successfulUploads.length > 0) {
                    setFormData(prev => ({
                      ...prev,
                      images: [...prev.images, ...successfulUploads]
                    }))
                    setMessage(`${successfulUploads.length} image(s) uploaded successfully`)
                    setMessageType('success')
                    setTimeout(() => setMessage(''), 3000)
                  } else {
                    setMessage('Failed to upload images')
                    setMessageType('error')
                  }
                } catch (error) {
                  setMessage('Error uploading images')
                  setMessageType('error')
                } finally {
                  setUploadingImages(false)
                  e.target.value = '' // Reset input
                }
              }}
              disabled={uploadingImages}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {uploadingImages && (
              <p className="mt-2 text-sm text-slate-500">Uploading images...</p>
            )}
            {formData.images && formData.images.length > 0 && (
              <div className="mt-3 grid grid-cols-4 gap-3">
                {formData.images.map((url, index) => (
                  <div key={index} className="relative group">
                    <img src={url} alt={`Offer ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-slate-200" />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          images: prev.images.filter((_, i) => i !== index)
                        }))
                      }}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Videos */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Videos
            </label>
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || [])
                if (files.length === 0) return

                setUploadingVideos(true)
                setMessage('')
                const uploadPromises = files.map(file => uploadOfferVideo(file))
                
                try {
                  const results = await Promise.all(uploadPromises)
                  const successfulUploads = results
                    .filter(res => res.status === 200 && res.data?.videoUrl)
                    .map(res => res.data.videoUrl)
                  
                  if (successfulUploads.length > 0) {
                    setFormData(prev => ({
                      ...prev,
                      videos: [...prev.videos, ...successfulUploads]
                    }))
                    setMessage(`${successfulUploads.length} video(s) uploaded successfully`)
                    setMessageType('success')
                    setTimeout(() => setMessage(''), 3000)
                  } else {
                    setMessage('Failed to upload videos')
                    setMessageType('error')
                  }
                } catch (error) {
                  setMessage('Error uploading videos')
                  setMessageType('error')
                } finally {
                  setUploadingVideos(false)
                  e.target.value = '' // Reset input
                }
              }}
              disabled={uploadingVideos}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {uploadingVideos && (
              <p className="mt-2 text-sm text-slate-500">Uploading videos...</p>
            )}
            {formData.videos && formData.videos.length > 0 && (
              <div className="mt-3 space-y-2">
                {formData.videos.map((url, index) => (
                  <div key={index} className="relative group flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <video src={url} className="w-32 h-20 object-cover rounded" controls={false} />
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 truncate">Video {index + 1}</p>
                      <p className="text-xs text-slate-400 truncate">{url}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          videos: prev.videos.filter((_, i) => i !== index)
                        }))
                      }}
                      className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/center/offers')}
              className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Offer' : 'Create Offer'}
            </button>
          </div>
        </form>
          </div>
        </main>
      </div>
    </div>
  )
}

export default OfferFormPage

